<script setup lang="ts">
  import { computed, onMounted, reactive, ref, useTemplateRef, watch } from 'vue'
  import { guard } from '~renderer/util/telefunc'
  import { onGetIslandByTurnipCode } from '~rpc/procedures/island/onGetIslandByTurnipCode.telefunc'
  import { IslandPublicView } from '~shared/types/island'
  import { UNAMBIGUOUS_ALPHABET } from '~shared/util/turnip'

  const rootEl = useTemplateRef<HTMLDivElement>('rootEl')
  const state = reactive({
    code: '',
    hasSubmitted: false,
    codeChecked: false,
    shake: false,
  })

  const island = ref<IslandPublicView | undefined>()
  const upperCaseCode = computed(() => state.code.toUpperCase())
  const error = computed(() => {
    const { value: code } = upperCaseCode
    const { hasSubmitted, codeChecked } = state

    // They haven't attempted a submit yet. Let's give them a chance
    if (!hasSubmitted) return

    // Trying to submit without typing anything
    if (hasSubmitted && !code.length) return 'Please enter a code'
    if (code.length !== 9) return 'Code must be 9 characters long'
    if (!code.split('').every((char) => UNAMBIGUOUS_ALPHABET.includes(char)))
      return 'Code contains invalid characters'
    if (codeChecked && !island.value) return 'Invalid or expired code'
  })

  const onJoinTriggered = async () => {
    state.hasSubmitted = true

    if (!error.value) {
      island.value = await guard(
        onGetIslandByTurnipCode({
          turnipCode: state.code,
        }),
      )
      state.codeChecked = true
    }

    if (error.value) {
      state.shake = true
      return
    }

    window.location.href = `/island/${upperCaseCode.value}`
  }

  const formattedCode = computed({
    get: () => {
      const chunks = state.code.match(/.{1,3}/g)
      return chunks ? chunks.join('-') : state.code
    },
    set: (val: string) => {
      state.code = val.replace(/-/g, '').slice(0, 9)
    },
  })

  // If they change the input lets give them another shot without error messages
  watch(
    () => state.code,
    () => {
      state.hasSubmitted = false
      state.codeChecked = false
      island.value = undefined
    },
  )

  onMounted(() =>
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }),
  )
</script>

<template>
  <div
    class="w-full max-w-2xl rounded-xl border border-red-400/80 bg-purple-500 p-8 text-white shadow-2xl"
    ref="rootEl"
    :class="{ shake: state.shake }"
    @animationend="state.shake = false"
  >
    <div class="flex h-full flex-col">
      <h2 class="font-display mb-2 text-center text-3xl font-bold">Join an Island</h2>

      <!-- Input Section -->
      <div class="my-12 flex flex-col items-center gap-4 text-center">
        <label for="code" class="text-lg opacity-80">
          Enter the Turnip Code of the island you want to join.
        </label>

        <input
          v-model="formattedCode"
          type="text"
          class="w-4/5 rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold tracking-widest uppercase focus:border-red-400 focus:outline-none lg:w-2/3"
          placeholder="XXX-XXX-XXX"
          maxlength="11"
          @keyup.enter="onJoinTriggered"
        />
        <div class="h-0">
          <Transition name="slide-in-out">
            <p class="pointer-events-none -mt-2 text-xs text-red-500" v-if="error">
              {{ error }}
            </p>
          </Transition>
        </div>
      </div>

      <!-- Action -->
      <div class="flex justify-center">
        <button
          @click="onJoinTriggered"
          class="cursor-pointer rounded-lg bg-green-400 px-8 py-2 font-bold text-black transition-colors hover:bg-green-500"
        >
          Join Island
        </button>
      </div>
    </div>
  </div>
</template>
